import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { getAuth } from 'firebase/auth';
import {
  SolicitacaoCreateSchema,
  SolicitacaoUpdateSchema,
  SolicitacaoOperacionalUpdateSchema,
  SolicitacaoCancelamentoSchema,
  SolicitacaoReativacaoSchema,
  SolicitacaoExclusaoSchema,
  SolicitacaoVincularColaboradorSchema,
} from '../models/solicitacao.schemas';
import type {
  Solicitacao,
  SolicitacaoCreate,
  SolicitacaoUpdate,
  SolicitacaoOperacionalUpdate,
  SolicitacaoCancelamento,
  SolicitacaoReativacao,
  SolicitacaoExclusao,
  SolicitacaoVincularColaborador,
} from '../models/solicitacao.types';

/** Mesma definição de "em aberto" já usada em getFilaTrabalho — nem concluída, nem cancelada. */
const STATUS_EM_ABERTO = ['aberta', 'em_andamento'] as const;

/**
 * O Firestore rejeita `undefined` em qualquer campo (erro "Unsupported field value:
 * undefined"). Campos opcionais do Zod (`.optional()`) preservam a chave com valor
 * `undefined` quando o input já veio com essa chave presente (ex.: `observacoes:
 * observacoes || undefined` no formulário) — este helper remove essas chaves antes do
 * `addDoc`/`updateDoc`, sem precisar mudar cada chamador individualmente.
 */
function semUndefined<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== undefined)) as T;
}

/**
 * Classe de serviço para operações Firestore relacionadas a Solicitações do módulo SI.
 *
 * Padrão de escrita direta ao Firestore (SDK client), com validação Zod antes de cada
 * operação — mesmo padrão já usado em objectiveService (OKR). A regra de negócio de
 * "quem pode fazer o quê" é imposta em firestore.rules; este serviço só evita
 * chamadas malformadas chegarem ao Firestore.
 */
class SolicitacaoServiceClass {
  private collectionName = 'si_solicitacoes';

  async create(data: SolicitacaoCreate): Promise<string> {
    const validated = SolicitacaoCreateSchema.parse(data);

    const docRef = await addDoc(collection(db, this.collectionName), semUndefined({
      ...validated,
      status: 'aberta',
      dataAbertura: serverTimestamp(),
      createdBy: {
        id: validated.solicitanteId,
        name: '', // preenchido pelo hook chamador, que tem acesso ao AppUser completo
        avatar: null,
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }));

    return docRef.id;
  }

  /** Campos de dados/contexto — permitido a SI, RH e Solicitante (ver firestore.rules). */
  async update(data: SolicitacaoUpdate): Promise<void> {
    const validated = SolicitacaoUpdateSchema.parse(data);
    const { id, ...fields } = validated;

    await updateDoc(doc(db, this.collectionName, id), semUndefined({
      ...fields,
      updatedAt: serverTimestamp(),
    }));
  }

  /**
   * Campos operacionais — exclusivo do SI (ver firestore.rules).
   *
   * Também avança 'aberta' -> 'em_andamento' quando necessário — mesmo raciocínio de
   * `movimentacaoService.avancarParaEmAndamentoSeNecessario`, mas disparado aqui porque
   * tipos sem Movimentação (Formatação, Acessos) nunca passam por lá: sem isso, ficariam
   * travados em "Novo" pra sempre, mesmo com o SI já mexendo neles (ex.: marcando um
   * acesso como concluído). "Novo" continua existindo de propósito — sinaliza pro SI que
   * ninguém começou a tratar ainda; só sai desse estágio quando alguém realmente age
   * (nunca só por abrir/visualizar). Nunca mexe se já estiver em qualquer outro status
   * (concluída/cancelada/excluída não devem "voltar" a em_andamento por uma edição).
   */
  async updateOperacional(data: SolicitacaoOperacionalUpdate): Promise<void> {
    const validated = SolicitacaoOperacionalUpdateSchema.parse(data);
    const { id, ...fields } = validated;

    const docRef = doc(db, this.collectionName, id);
    const snap = await getDoc(docRef);
    const avancaStatus = snap.exists() && snap.data().status === 'aberta';

    await updateDoc(docRef, semUndefined({
      ...fields,
      ...(avancaStatus ? { status: 'em_andamento' } : {}),
      updatedAt: serverTimestamp(),
    }));
  }

  /** Cancelamento — SI ou o próprio solicitante (ver firestore.rules). Não apaga histórico. */
  async cancelar(data: SolicitacaoCancelamento): Promise<void> {
    const validated = SolicitacaoCancelamentoSchema.parse(data);

    await updateDoc(doc(db, this.collectionName, validated.id), {
      status: 'cancelada',
      canceledBy: validated.canceledBy,
      motivoCancelamento: validated.motivoCancelamento,
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Reativação — mesma Solicitação, mesmo histórico (Discovery §14). Não cria uma nova
   * Solicitação nem desfaz Movimentações já concluídas.
   */
  async reativar(data: SolicitacaoReativacao): Promise<void> {
    const validated = SolicitacaoReativacaoSchema.parse(data);

    await updateDoc(doc(db, this.collectionName, validated.id), {
      status: 'em_andamento',
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Exclusão (soft-delete) — exclusiva do SI (ver firestore.rules). Não apaga o
   * documento (`allow delete: if false` — nunca há delete físico), só muda o status pra
   * `'excluida'`, o que já basta pra sumir das listas/dashboards por padrão (filtro
   * inicial de status não inclui `'excluida'`, ver SolicitacoesListPage).
   */
  async excluir(data: SolicitacaoExclusao): Promise<void> {
    const validated = SolicitacaoExclusaoSchema.parse(data);

    await updateDoc(doc(db, this.collectionName, validated.id), semUndefined({
      status: 'excluida',
      excludedBy: validated.excludedBy,
      motivoExclusaoTipo: validated.motivoExclusaoTipo,
      motivoExclusao: validated.motivoExclusao ?? null,
      solicitacaoRelacionadaId: validated.solicitacaoRelacionadaId ?? undefined,
      updatedAt: serverTimestamp(),
    }));
  }

  /**
   * "Vincular Colaborador" — só a transição colaboradorId null → id real, exclusiva de
   * si_admin (ver firestore.rules). Bloqueia se o colaborador já tiver outra Admissão
   * "em aberto" (aberta ou em_andamento — mesma definição de getFilaTrabalho) para evitar
   * duas Admissões abertas apontando para a mesma pessoa.
   *
   * Checagem de duplicidade é feita aqui no client, não em firestore.rules — Rules não
   * conseguem consultar "existe outro documento com tal campo" (só avaliam o documento
   * sendo escrito), então esta é a única camada real dessa validação específica.
   */
  async vincularColaborador(data: SolicitacaoVincularColaborador): Promise<void> {
    const validated = SolicitacaoVincularColaboradorSchema.parse(data);

    const admissoesAbertas = await getDocs(
      query(
        collection(db, this.collectionName),
        where('colaboradorId', '==', validated.colaboradorId),
        where('tipoSolicitacao', '==', 'admissao'),
        where('status', 'in', [...STATUS_EM_ABERTO])
      )
    );

    if (!admissoesAbertas.empty) {
      throw new Error('Este colaborador já possui uma Admissão em aberto vinculada a ele.');
    }

    await updateDoc(doc(db, this.collectionName, validated.id), {
      colaboradorId: validated.colaboradorId,
      colaboradorEmail: validated.colaboradorEmail,
      colaboradorNome: validated.colaboradorNome,
      updatedBy: validated.updatedBy,
      updatedAt: serverTimestamp(),
    });
  }

  async getById(id: string): Promise<Solicitacao | null> {
    const snap = await getDoc(doc(db, this.collectionName, id));
    if (!snap.exists()) {
      return null;
    }
    return { id: snap.id, ...snap.data() } as Solicitacao;
  }

  /** "Solicitações que eu abri" — Discovery §10.2, sem necessidade de índice reverso. */
  async getBySolicitante(solicitanteId: string): Promise<Solicitacao[]> {
    const q = query(
      collection(db, this.collectionName),
      where('solicitanteId', '==', solicitanteId),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Solicitacao);
  }

  /**
   * Time RH (Portal do SI): o que qualquer pessoa do time abriu, pelo e-mail de quem abriu.
   * Sem orderBy de propósito (`in` + orderBy pediria índice composto) — quem chama ordena.
   * Lotes de 30 (limite do `in` do Firestore).
   */
  async getBySolicitanteEmails(emails: string[]): Promise<Solicitacao[]> {
    const lotes: string[][] = [];
    for (let i = 0; i < emails.length; i += 30) lotes.push(emails.slice(i, i + 30));
    const resultados = await Promise.all(
      lotes.map((lote) => getDocs(query(collection(db, this.collectionName), where('solicitanteEmail', 'in', lote))))
    );
    return resultados.flatMap((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Solicitacao));
  }

  /**
   * "Minha Equipe" — Solicitações onde o usuário logado é o Responsável Raro da
   * alocação específica referenciada (não o responsável geral do perfil do colaborador,
   * que pode ser outra pessoa — colaborador pode estar em vários projetos com gestores
   * diferentes, ver `Solicitacao.alocacaoResponsavelId`). Solicitações sem alocação
   * vinculada (dado antigo, ou tipos sem alocação como Admissão) não aparecem aqui até
   * alguém do SI regularizar (ver AlocacaoResponsavelSection) — decisão de produto.
   */
  async getByAlocacaoResponsavel(responsavelId: string): Promise<Solicitacao[]> {
    // Alocação por Movimentação: entra quem é responsável de QUALQUER Movimentação da
    // Solicitação (alocacaoResponsavelEmails, mantida pela function). A busca antiga, pela
    // alocação da Solicitação, continua junto pras solicitações ainda não migradas.
    const email = getAuth().currentUser?.email?.toLowerCase();
    const [antigas, porMovimentacao] = await Promise.all([
      getDocs(
        query(collection(db, this.collectionName), where('alocacaoResponsavelId', '==', responsavelId), orderBy('createdAt', 'desc'))
      ),
      email
        ? getDocs(query(collection(db, this.collectionName), where('alocacaoResponsavelEmails', 'array-contains', email)))
        : Promise.resolve(null),
    ]);
    const porId = new Map<string, Solicitacao>();
    [...antigas.docs, ...(porMovimentacao?.docs ?? [])].forEach((d) => porId.set(d.id, { id: d.id, ...d.data() } as Solicitacao));
    return Array.from(porId.values()).sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
  }

  /**
   * Fila de trabalho do SI — todas as Solicitações, mais antigas primeiro. Não filtra por
   * status na query (nenhum índice composto necessário, `orderBy` num campo só): o SI
   * também precisa revisar solicitações concluídas/canceladas por essa aba (Discovery —
   * "Controle de Solicitações"), então o filtro "Mostrar somente em aberto" é só client-side
   * (ver SolicitacoesListPage), igual já funciona nas abas "Que eu abri"/"Minha Equipe".
   */
  async getFilaTrabalho(): Promise<Solicitacao[]> {
    const q = query(collection(db, this.collectionName), orderBy('createdAt', 'asc'));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Solicitacao);
  }
}

export const solicitacaoService = new SolicitacaoServiceClass();
